import React from "react";
import {
  Box,
  Button,
  ColumnLayout,
  Container,
  Header,
  HelpPanel,
  Link,
} from "@amzn/awsui-components-react-v3";
import Skeleton from "@amzn/awsui-components-console/skeleton";
import { AmazonTeamsHomeUrl, SupportUrls } from "../lib/constants";

const headerTitle = "Groups";

interface GroupsOverviewProps {
  groups?: string[];
  showSkeleton: boolean;
  onInfoClick: (panel: React.ReactNode) => void;
}

const helpPanel = (
  <HelpPanel header={<Header variant="h2">Groups</Header>}>
    <p>
      Group membership changes are synced from{" "}
      <Link external href={AmazonTeamsHomeUrl}>
        Amazon Teams
      </Link>{" "}
      within 30 minutes.
    </p>
    <p>
      Note that only specific groups are synced to Frontier. If you would like
      us to sync additional groups, please{" "}
      <Link external href={SupportUrls.GROUPS_CONFIGURATION_REQUEST}>
        open a ticket
      </Link>
      .
    </p>
  </HelpPanel>
);

const GroupsSkeleton = () => (
  <Container
    data-testid="groups-overview-skeleton"
    header={<Header variant="h3">{headerTitle}</Header>}
  >
    <ColumnLayout columns={4} variant="text-grid">
      <Skeleton variant="text-body-m" width="80%" />
      <Skeleton variant="text-body-m" width="60%" />
      <Skeleton variant="text-body-m" width="70%" />
      <Skeleton variant="text-body-m" width="50%" />
    </ColumnLayout>
  </Container>
);

export const GroupsOverview = ({
  groups,
  showSkeleton,
  onInfoClick,
}: GroupsOverviewProps) => {
  if (showSkeleton) {
    return <GroupsSkeleton />;
  }

  return (
    <Container
      header={
        <Header
          variant="h3"
          counter={`(${(groups ?? []).length})`}
          info={
            <Button
              variant="inline-link"
              onClick={() => onInfoClick(helpPanel)}
            >
              Info
            </Button>
          }
          data-testid="user-groups-section"
        >
          {headerTitle}
        </Header>
      }
    >
      {groups && groups.length > 0 ? (
        <ColumnLayout columns={4} variant="text-grid" data-testid="user-groups">
          {[...groups].sort().map((group) => (
            <div key={group}>{group}</div>
          ))}
        </ColumnLayout>
      ) : (
        <Box color="text-status-inactive" data-testid="no-groups">
          No groups found
        </Box>
      )}
    </Container>
  );
};
